import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FaTimes, FaTrash } from "react-icons/fa";
import eventUploadImage from "assets/images/others/host-side-images/uploadEventImage.png";
import { ReactComponent as InforCircle } from "assets/images/svgs/info-circle.svg";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import {
  EventDTO,
  WorkshopSessionDTO,
  CreateEventPayloadDTO,
} from "utils/helpers/models/shared/event.dto";
import FormErrorMessage from "components/forms/form-error-message";
import {
  AGE_GROUPS,
  EVENT_STATUS,
  LANGUAGES,
  SKILL_LEVELS,
} from "utils/helpers/enums/shared.enum";
import { IoCloseSharp } from "react-icons/io5";
import { BadgeDTO } from "utils/helpers/models/host-dtos/badge.dto";
import useHost from "../useHooks";
import { RsvpDTO } from "utils/helpers/models/shared/rsvp.dto";
import AddRepresentativeModal from "../my-representatives/component/AddRepresentativeModal";
import {
  confirmationPopup,
  warningToaster,
  infoPopup,
} from "utils/helpers/common/alert-service";
import useStore from "hooks/useStore";
import useAuth from "containers/auth/useHooks";
import { addHours, differenceInMinutes, isAfter, parseISO } from "date-fns";
import BadgeModal from "./components";
import useUtils from "hooks/useUtils";
import { SUBSCRIPTION_TYPES } from "utils/helpers/enums/shared.enum";

const CreateEvent: React.FC = () => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.home },
    { title: "Create an Experiences", path: siteRoutes.createExperience },
  ];

  // Helper function to convert EventDTO to CreateEventPayloadDTO
  const convertToPayload = (eventData: EventDTO): CreateEventPayloadDTO => {
    return new CreateEventPayloadDTO({
      eventId: eventData.id || 0,
      title: eventData.title,
      description: eventData.description,
      location: eventData.location,
      ageGroup: eventData.ageGroup,
      skillLevel: eventData.skillLevel,
      coreConceptIds: eventData.coreConceptIds,
      joineeSlots: eventData.joineeSlots,
      parentSupervision: eventData.parentSupervision,
      language: eventData.language,
      physicalConsideration: eventData.physicalConsideration,
      specialInstructions: eventData.specialInstructions,
      badgeUrlId: eventData.badgeUrlId || 0,
      rsvpEmail: eventData.rsvpEmail,
      eventPicsIds: eventData.eventPicsIds,
      ambassadorSupportRequired: eventData.ambassadorSupportRequired,
      entryFee: eventData.entryFee,
      isSession: eventData.isSession,
      workshopSessions: eventData.workshopSessions.map((session) => ({
        startDateTime: session.startDateTime,
        endDateTime: session.endDateTime,
      })),
      ambassadosAllowed: eventData.ambassadosAllowed,
      ambassadorOneCode: eventData.ambassadorOneCode,
      ambassadorTwoCode: eventData.ambassadorTwoCode,
      ambassadorReferralCode: eventData.ambassadorReferralCode,
    });
  };
  const [isSkillLevelTooltipVisible, setIsSkillLevelTooltipVisible] =
    useState(false);
  const [isOpenBadgeModal, setIsOpenBadgeModal] = useState(false);
  const [coreConcepts, setCoreConcepts] = useState({
    all: [],
    exception: [],
    appropriate: [],
  });

  const { getRsvps, createEvent, getEventById, getCoreConcept } = useHost();
  const { uploadFile } = useAuth();
  const { getQueryParams } = useUtils();
  const params = getQueryParams();
  const { getBusinessAddress, isProfileCompleted, getUser, getHostTotalEvents } = useStore();
  const navigate = useNavigate();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.home, { replace: true });
    }
  }, [isProfileCompleted, navigate]);
  const location = getBusinessAddress();
  const [formData, setFormData] = useState<EventDTO>(
    new EventDTO({ location, parentSupervision: true })
  );
  const [rsvps, setRsvps] = useState<RsvpDTO[]>([]);
  const [openRsvpDialog, setOpenRsvpDialog] = useState<boolean>(false);
  const [coreConceptSelectValue, setCoreConceptSelectValue] = useState<string>("");

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<EventDTO>({ defaultValues: formData });

  const onSubmit = async (state: EVENT_STATUS = EVENT_STATUS.POSTED) => {
    // Check subscription limits before submission
    const userData = getUser();
    const subscription = userData?.user?.subscription;
    const subscriptionType = subscription?.product?.subscriptionType;
    const features = subscription?.product?.features || subscription?.features;
    
    if (subscriptionType === SUBSCRIPTION_TYPES.BASIC) {
      if (formData.isSession) {
        // For sessions: Check if total sessions < allowed sessions limit
        const sessionLimit = parseInt(features?.sessions || "2", 10);
        const totalSessions = getHostTotalEvents();
        
        if (totalSessions >= sessionLimit) {
          await infoPopup(
            "Session Limit Reached",
            `You have reached your session limit of ${sessionLimit} sessions. Please upgrade your subscription to create more sessions.`
          );
          return;
        }
      } else {
        // For workshops: Check if postWorkshop feature is enabled
        const postWorkshop = features?.postWorkshop || features?.post_workshop || false;
        
        if (!postWorkshop) {
          await infoPopup(
            "Workshop Feature Not Available",
            "You have not unlocked the workshop feature. Please upgrade your subscription to unlock workshop feature."
          );
          return;
        }
      }
    }
    
    if (!formData.eventPicsIds.length && !formData.eventPicsUrl.length) {
      warningToaster("Please upload atleast one event image");
      return;
    }
    
    // Ensure maximum 3 images limit
    if (formData.eventPicsIds.length > 3 || formData.eventPicsUrl.length > 3) {
      warningToaster("Maximum 3 images allowed. Please remove excess images.");
      return;
    }
    
    if (formData.eventPicsIds.length > 0 || formData.eventPicsUrl.length > 0) {
      // Update formData with converted values (these are safe to modify)
      formData.availableSlots = +formData.availableSlots;
      formData.joineeSlots = +formData.availableSlots;
      formData.entryFee = +formData.entryFee;
      formData.ambassadosAllowed = +formData.ambassadosAllowed;
      formData.ambassadorSupportRequired = Boolean(
        formData.ambassadorSupportRequired
      );

      // Create a deep copy of formData for payload conversion (don't modify original formData)
      const payloadData: EventDTO = {
        ...formData,
        workshopSessions: formData.workshopSessions.map((session) => ({
          ...session,
          // Convert datetime to UTC only for the payload, keep original in formData
          startDateTime: session.startDateTime
            ? new Date(session.startDateTime).toISOString()
            : session.startDateTime,
          endDateTime: session.endDateTime
            ? new Date(session.endDateTime).toISOString()
            : session.endDateTime,
        })),
      };

      // Convert to payload DTO
      const payload = convertToPayload(payloadData);

      if (state === EVENT_STATUS.DRAFT) {
        const confirmation = await confirmationPopup(
          "When saving for later, time and dates won't be saved",
          "Ok",
          "Cancel"
        );

        if (confirmation.isConfirmed) {
          createEvent(payload, state);
        }
      } else {
        createEvent(payload, state);
      }
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;

    const currentImageCount = formData.eventPicsIds.length;
    const maxImages = 3;
    const remainingSlots = maxImages - currentImageCount;

    // Check if limit is already reached
    if (remainingSlots <= 0) {
      warningToaster(`Maximum ${maxImages} images allowed. Please remove an image first.`);
      event.target.value = '';
      return;
    }

    // Process all selected files
    const fileArray = Array.from(files);

    // Always limit to remaining slots, regardless of how many files were selected
    const filesToProcess = fileArray.slice(0, remainingSlots);
    
    // Show warning if user tried to upload more than allowed
    if (fileArray.length > remainingSlots) {
      warningToaster(
        `You can only upload ${remainingSlots} more image(s). Maximum ${maxImages} images allowed. Only ${remainingSlots} image(s) will be uploaded.`
      );
    }
    
    // Process only the allowed number of files
    for (const file of filesToProcess) {
      const picture = await uploadFile(file);
      if (picture) {
        formData.eventPicsIds.push(picture.id);
        formData.eventPicsUrl.push(picture);
      }
    }

    // Update form values after all uploads
    if (filesToProcess.length > 0) {
      setValue("eventPicsIds", formData.eventPicsIds);
      setFormData({ ...formData });
    }

    // Reset input value so user can select the same files again if needed
    event.target.value = '';
  };

  const removeImage = (index: number) => {
    formData.eventPicsIds.splice(index, 1);
    formData.eventPicsUrl.splice(index, 1);
    setValue("eventPicsIds", formData.eventPicsIds);
    setFormData({ ...formData });
  };

  const handleChange = (event: any) => {
    const {
      target: { name, value },
    } = event;
    if (name === "parentSupervision") {
      formData[name] = JSON.parse(value);
    } else {
      formData[name] = value;
    }
    if (name === "skillLevel") {
      formData.coreConceptIds = [];
      setValue("coreConceptIds", formData.coreConceptIds);
      // Reset select dropdown when skill level changes
      setCoreConceptSelectValue("");
    }

    // Auto-set parent supervision based on age group
    if (name === "ageGroup") {
      if (value === AGE_GROUPS.GRADES_9_TO_12) {
        // For grades 9-12, allow user to change (keep current value or default to true)
        if (
          formData.parentSupervision === undefined ||
          formData.parentSupervision === null
        ) {
          formData.parentSupervision = true;
          setValue("parentSupervision", true);
        }
      } else {
        // For younger age groups, always set to true (Guardian Presence Required)
        formData.parentSupervision = true;
        setValue("parentSupervision", true);
      }
    }

    setValue(name, formData[name], { shouldValidate: true });
    setFormData({ ...formData });
  };

  const handleSessionTypeChange = async (isSession: boolean) => {
    if (!params?.id) {
      const userData = getUser();
      const subscription = userData?.user?.subscription;
      const subscriptionType = subscription?.product?.subscriptionType;
      const features = subscription?.product?.features || subscription?.features;
      
      // Check if subscription type is BASIC
      if (subscriptionType === SUBSCRIPTION_TYPES.BASIC) {
        if (isSession) {
          // For sessions: Check if total sessions < allowed sessions limit
          const sessionLimit = parseInt(features?.sessions || "2", 10);
          const totalSessions = getHostTotalEvents();
          
          if (totalSessions >= sessionLimit) {
            await infoPopup(
              "Session Limit Reached",
              `You have reached your session limit of ${sessionLimit} sessions. Please upgrade your subscription to create more sessions.`
            );
            return; // Prevent changing to session
          }
          
          // Allow session creation
          formData.workshopSessions = [new WorkshopSessionDTO()];
        } else {
          // For workshops: Check if postWorkshop feature is enabled
          const postWorkshop = features?.postWorkshop || features?.post_workshop || false;
          
          if (!postWorkshop) {
            warningToaster("You have not unlocked the workshop feature. Please upgrade your subscription to unlock workshop feature.");
            return; // Prevent changing to workshop
          }
          
          // Allow workshop creation
          formData.workshopSessions = [
            new WorkshopSessionDTO(),
            new WorkshopSessionDTO(),
          ];
        }
      } else {
        // For non-BASIC subscriptions, allow both session and workshop
        if (isSession) {
          formData.workshopSessions = [new WorkshopSessionDTO()];
        } else {
          formData.workshopSessions = [
            new WorkshopSessionDTO(),
            new WorkshopSessionDTO(),
          ];
        }
      }
      
      setValue("isSession", isSession);
      setValue("workshopSessions", formData.workshopSessions);
      setFormData({ ...formData, isSession });
    }
  };

  const handleRemoveCoreConcept = (index: number) => {
    formData.coreConceptIds.splice(index, 1);
    setValue("coreConceptIds", formData.coreConceptIds);
    setFormData({ ...formData });
    // Reset select dropdown when all concepts are removed
    if (formData.coreConceptIds.length === 0) {
      setCoreConceptSelectValue("");
    }
  };

  const handleChangeAmbassadorSupport = (event: any) => {
    const {
      target: { value },
    } = event;
    formData.ambassadorSupportRequired = JSON.parse(value);
    setValue("ambassadorSupportRequired", formData.ambassadorSupportRequired);
    if (!formData.ambassadorSupportRequired) {
      formData.ambassadorOneCode = "";
      formData.ambassadorTwoCode = "";
      setValue("ambassadorOneCode", formData.ambassadorOneCode);
      setValue("ambassadorTwoCode", formData.ambassadorTwoCode);
    }
    setFormData({ ...formData });
  };

  const validateStartDate = (startDateTime: string) => {
    if (!startDateTime) return "Start Date & Time is required";

    const currentDate = new Date();
    const minValidDate = addHours(currentDate, 48);

    if (!isAfter(parseISO(startDateTime), minValidDate)) {
      return "Start date & time must be at least 48 hours later";
    }

    return true;
  };

  const validateEndDate = (endDateTime: string, index: number) => {
    if (!endDateTime) return "End Date & Time is required";

    const startDateTime = formData.workshopSessions[index]?.startDateTime;
    if (!startDateTime) return "Select Start Date first";

    const start = parseISO(startDateTime);
    const end = parseISO(endDateTime);

    if (!isAfter(end, start)) {
      return "End date & time must be after Start date & time";
    }

    if (differenceInMinutes(end, start) < 20) {
      return "Duration must be at least 20 minutes";
    }

    return true;
  };

  const handleChangeSession = (event: any, index: number) => {
    const { name, value } = event.target;
    const splittedName = name.split(".");
    const formDataName = splittedName[splittedName.length - 1];
    setValue(name, value, { shouldValidate: true });
    formData.workshopSessions[index][formDataName] = value;
    setFormData({ ...formData });
  };

  const addSession = () => {
    formData.workshopSessions.push(new WorkshopSessionDTO());
    setValue("workshopSessions", formData.workshopSessions);
    setFormData({ ...formData });
  };

  const removeSession = (index: number) => {
    formData.workshopSessions.splice(index, 1);
    setValue("workshopSessions", formData.workshopSessions);
    setFormData({ ...formData });
  };

  useEffect(() => {
    getRsvps(setRsvps);
    if (params?.id) {
      const isDraft = params?.draft === "true";
      getEventById(params?.id, setFormData, setValue, isDraft);
    }
    register("coreConceptIds", { required: true });
    register("badgeUrlId", {
      required: "Badge upload is required",
      validate: (value) => value > 0 || "Please upload a badge",
    });
  }, []);

  const handleCloseBadgeDialog = (eventBadge?: BadgeDTO) => {
    setIsOpenBadgeModal(false);
    if (eventBadge) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        badgeUrlId: eventBadge.id,
        eventBadge,
      }));
      setValue("badgeUrlId", eventBadge.id, { shouldValidate: true });
    }
  };

  // get core concept

  useEffect(() => {
    const body = { page: 0, size: 50 };

    getCoreConcept(body, (res) => {
      const exception = res.filter((item) => item.type === "exception_learner");
      const appropriate = res.filter(
        (item) => item.type === "appropriate_for_age_level"
      );

      setCoreConcepts({
        all: res,
        exception,
        appropriate,
      });
    });
  }, []);

  // Filter options according to selected skill level
  const getCoreConceptOptions = () => {
    if (formData.skillLevel === SKILL_LEVELS.EXCEPTIONAL_LEARNER) {
      return coreConcepts.exception; // return full objects
    }
    if (formData.skillLevel === SKILL_LEVELS.APPROPRIATE_FOR_AGE_LEVEL) {
      return coreConcepts.appropriate;
    }
    if (formData.skillLevel === SKILL_LEVELS.BOTH) {
      return coreConcepts.all;
    }
    return [];
  };

  const handleCoreConceptChange = (event: any) => {
    const selectedValue = event.target.value;
    if (!selectedValue) return; // Don't process if empty value
    
    if (formData.coreConceptIds.length < 3) {
      const conceptId = Number(selectedValue);
      if (!formData.coreConceptIds.includes(conceptId)) {
        formData.coreConceptIds.push(conceptId);
        setValue("coreConceptIds", formData.coreConceptIds, {
          shouldValidate: true,
        });
        setFormData({ ...formData });
        // Reset select dropdown after adding concept
        setCoreConceptSelectValue("");
      }
    } else {
      warningToaster("Core concepts cannot be more than 3");
      // Reset select dropdown if limit reached
      setCoreConceptSelectValue("");
    }
  };

  

  // Get subscription info for UI restrictions
  const userData = getUser();
  const subscription = userData?.user?.subscription;
  const subscriptionType = subscription?.product?.subscriptionType;
  const features = subscription?.product?.features || subscription?.features;
  const isBasicSubscription = subscriptionType === SUBSCRIPTION_TYPES.BASIC;
  const postWorkshop = features?.postWorkshop || features?.post_workshop || false;
  // Allow clicking workshop button but show toaster if not allowed
  const canCreateWorkshop = !isBasicSubscription || postWorkshop;

  return (
    <div className="container">
      {/* Radio Buttons */}
      <Breadcrumb links={breadcrumbLinks} />
      <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-4">
        <label className="flex items-center space-x-2">
          <input
            type="radio"
            name="sessionType"
            value="Individual Session"
            disabled={params?.id}
            checked={formData.isSession}
            onChange={() => handleSessionTypeChange(true)}
            className="custom-radio disabled:opacity-50"
          />
          <span>Individual Session</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="radio"
            name="sessionType"
            disabled={params?.id}
            value="Workshop"
            checked={!formData.isSession}
            onChange={() => handleSessionTypeChange(false)}
            className="custom-radio disabled:opacity-50"
          />
          <span>Workshop</span>
        </label>
      </div>

      {/* Form */}
      <form className="space-y-4 py-6">
        <h1 className="text-lg font-bold text-black">
          {formData.isSession ? "Individual Session" : "Workshop"}
        </h1>
        <div className="">
          <label className="text-label text-base font-semibold">
            Upload Event Images
          </label>

          <div className="flex space-x-4 mt-4">
            {formData.eventPicsUrl.map((src, index) => (
              <div key={index} className="relative w-[106px] h-[82px]">
                <img
                  src={src.fileDownloadUri}
                  alt={`Uploaded ${index}`}
                  className="w-full h-full rounded-lg object-cover"
                />
                <button
                  type="button"
                  className="absolute top-0 right-0 bg-yellow-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                  onClick={() => removeImage(index)}
                >
                  <FaTimes size={12} />
                </button>
              </div>
            ))}
            {formData.eventPicsUrl.length < 3 && (
              <div className="w-[106px] h-[82px]">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleImageUpload(e)}
                  />
                  {/* <div> */}
                  <img
                    className="w-[106px] h-[82px]"
                    src={eventUploadImage}
                    alt="eventUploadImage"
                  />
                  {/* </div> */}
                </label>
                <FormErrorMessage error={errors.eventPicsUrl} />
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Title Field */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Enter Title
            </label>
            <input
              type="text"
              className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
              {...register("title", {
                required: "Title is required",
              })}
              value={formData.title}
              onChange={handleChange}
            />
            {errors.title && (
              <p className="text-red-500 text-sm">{errors.title.message}</p>
            )}
          </div>
        </div>
        <div className="grid  grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Brief Description of the Experience
            </label>
            <textarea
              className="w-full  h-[100px] px-3 py-1 rounded-lg border border-gray-300 outline-none resize-none"
              {...register("description", {
                required: "Description is required",
              })}
              value={formData.description}
              onChange={handleChange}
            ></textarea>
        
            {errors.description && (
              <p className="text-red-500 text-sm">{errors.description.message}</p>
            )}
            
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Event Location
            </label>
            <input
              type="text"
              className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
              {...register("location", {
                required: "Location is required",
              })}
              value={formData.location}
              readOnly={location ? true : false}
              onChange={
                !location
                  ? handleChange
                  : () => console.warn("Do not fill this field")
              }
            />
           
            {errors.location && (
              <p className="text-red-500 text-sm">{errors.location.message}</p>
            )}
          </div>
        </div>
        <div className="relative">
          <label className="text-label text-base font-semibold flex gap-2 items-center">
            Skill Level
            <span
              className="cursor-pointer text-gray-500 hover:text-gray-700 relative"
              onMouseEnter={() => setIsSkillLevelTooltipVisible(true)}
              onMouseLeave={() => setIsSkillLevelTooltipVisible(false)}
            >
              <InforCircle />
            </span>
          </label>
          {/* Tooltip */}
          {isSkillLevelTooltipVisible && (
            <div className="absolute w-[400px] bottom-18 left-20 p-4 rounded-t-lg rounded-br-lg bg-primaryBlueBg text-white text-xs opacity-100 transition-opacity duration-300 z-10">
              <div className="flex flex-col gap-1">
                <h2 className="font-medium text-sm ">Age Level</h2>
                <p className="text-xs">
                  This experience is designed with activities and challenges
                  that match typical skill levels and development stages for a
                  specific age range. The content, pace, and materials are
                  age-appropriate to help learners succeed and feel confident.
                </p>
                <h2 className="font-medium text-sm mt-2">
                  Exceptional Learner
                </h2>
                <p className="text-xs">
                  This experience is tailored to support exceptional learners
                  who may benefit from specific accommodations or adjustments,
                  such as sensory adaptations, extra assistance, or a more
                  flexible approach. The environment and materials are selected
                  to help create an engaging, supportive setting for diverse
                  learning needs.
                </p>
                <h2 className="font-medium text-sm mt-2">Both</h2>
                <p className="text-xs">
                  This experience welcomes all age groups & skill levels. While
                  every effort will be made to adapt to the needs of a diverse
                  group, parents or guardians should assess whether the
                  experience aligns with their child's learning preferences &
                  needs.
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 mt-3">
            {Object.values(SKILL_LEVELS).map((level, index) => {
              return (
                <label className="flex items-center space-x-2" key={index}>
                  <input
                    type="radio"
                    {...register("skillLevel", {
                      required: "Skill level is required",
                    })}
                    value={level}
                    // checked={formData.skillLevel === level}
                    className="custom-radio"
                    onChange={handleChange}
                  />
                  <span className="capitalize">
                    {level.replaceAll("_", " ").toLowerCase()}
                  </span>
                </label>
              );
            })}
          </div>
           {errors.skillLevel && (
              <p className="text-red-500 text-sm">{errors.skillLevel.message}</p>
            )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Core Concept
            </label>

            {/* <select
              onChange={handleCoreConceptChange}
              className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
            >
              <option value="" selected>
                Select Core Concept
              </option>
              {getCoreConceptOptions().map((concept, index) => (
                <option key={index} value={concept} className="capitalize">
                  {concept.replaceAll("_", " ").toLowerCase()}
                </option>
              ))}
            </select> */}
            <select
              value={coreConceptSelectValue}
              onChange={handleCoreConceptChange}
              className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
            >
              <option value="">
                Select Core Concept
              </option>
              {getCoreConceptOptions().map((concept, index) => (
                <option key={index} value={concept.id} className="capitalize">
                  {concept.name}
                </option>
              ))}
            </select>

            <FormErrorMessage error={errors.coreConceptIds} />

            {/* Display selected core concepts */}
            {/* <div className="mt-2 flex gap-1 flex-wrap sm:flex-nowrap">
              {formData.coreConcept.map((concept, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 border p-1 rounded bg-yellowBg"
                >
                  <span className="text-sm text-blackie whitespace-nowrap capitalize"> 
                    {concept}

                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveCoreConcept(index)}
                    className="text-red-500 text-base"
                  >
                    <IoCloseSharp />
                  </button>
                </div>
              ))}
            </div> */}
            <div className="mt-2 flex gap-1 flex-wrap sm:flex-nowrap">
              {formData.coreConceptIds.map((conceptId, index) => {
                // find the name by id
                const conceptObj = coreConcepts.all.find(
                  (c) => c.id === conceptId
                );
                return (
                  <div
                    key={index}
                    className="flex items-center gap-2 border p-1 rounded bg-yellowBg"
                  >
                    <span className="text-sm text-blackie whitespace-nowrap capitalize">
                      {conceptObj?.name || conceptId}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveCoreConcept(index)}
                      className="text-red-500 text-base"
                    >
                      <IoCloseSharp />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {formData.workshopSessions.map((session, index) => (
            <div key={index}>
              <div className="flex justify-between mb-3 w-full md:w-[65%] lg:w-[49%] items-end">
                <h3 className="text-lg font-semibold">
                  {!formData.isSession ? (
                    <Fragment>Session {index + 1} Date & Time</Fragment>
                  ) : (
                    <Fragment>Session Date & Time</Fragment>
                  )}
                </h3>
                <div>
                  {index > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSession(index)}
                      className="text-red-500"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div key={index}>
                  <label className="block text-sm font-medium text-gray-700">
                    Select Start Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    {...register(`workshopSessions.${index}.startDateTime`, {
                      required: "Start Date is required",
                      validate: validateStartDate,
                    })}
                    value={session.startDateTime}
                    onChange={(e) => handleChangeSession(e, index)}
                    className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
                  />
                  {errors?.workshopSessions?.[index]?.startDateTime && (
                    <span className="text-red-500 text-sm">
                      {errors.workshopSessions[index].startDateTime.message}
                    </span>
                  )}
                </div>

                <div key={`end-${index}`}>
                  <label className="block text-sm font-medium text-gray-700">
                    Select End Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    {...register(`workshopSessions.${index}.endDateTime`, {
                      required: "End Date is required",
                      validate: (value) => validateEndDate(value, index),
                    })}
                    value={session.endDateTime}
                    onChange={(e) => handleChangeSession(e, index)}
                    className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
                  />
                  {errors?.workshopSessions?.[index]?.endDateTime && (
                    <span className="text-red-500 text-sm">
                      {errors.workshopSessions[index].endDateTime.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          {!formData.isSession && (
            <button
              type="button"
              onClick={addSession}
              className=" py-3 whitespace-nowrap w-[150px] bg-fade text-primaryBlueText rounded-lg mt-3"
            >
              Add New Session
            </button>
          )}
        </div>

        <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Slots Available
            </label>
            <input
              type="number"
              className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
              {...register("availableSlots", {
                required: "Slots Available is required",
                min: {
                  value: 0,
                  message: " Slots Available cannot be negative",
                },
                valueAsNumber: true,
              })}
              value={formData.availableSlots}
              onChange={handleChange}
            />
            {errors.availableSlots && (
              <p className="text-red-500 text-sm">{errors.availableSlots.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Age Group
            </label>
            <select
              {...register("ageGroup", {
                required: "Age Group is required",
              })}
              value={formData.ageGroup}
              onChange={handleChange}
              className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
            >
              <option value="">Select Age Group</option>
              {Object.values(AGE_GROUPS).map((group, index) => {
                return (
                  <option key={index} className="capitalize" value={group}>
                    {group.replaceAll("_", " ").toUpperCase()}
                  </option>
                );
              })}
            </select>
            {errors.ageGroup && (
              <p className="text-red-500 text-sm">{errors.ageGroup.message}</p>
            )}
          </div>
        </div>
        <div className="w-full grid grid-cols sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Parent Supervision
            </label>
            <select
              {...register("parentSupervision", {
                required: false,
              })}
              value={formData.parentSupervision?.toString() || "true"}
              onChange={handleChange}
              disabled={formData.ageGroup !== AGE_GROUPS.GRADES_9_TO_12}
              className={`w-full h-[51px] px-2 rounded-lg border border-gray-300 outline-none ${
                formData.ageGroup !== AGE_GROUPS.GRADES_9_TO_12
                  ? "bg-gray-100 cursor-not-allowed"
                  : ""
              }`}
            >
              <option value="true">Guardian Presence Required</option>
              <option value="false">Drop Off</option>
            </select>
            {errors.parentSupervision && (
              <span className="text-red-500 text-sm">
                {errors.parentSupervision.message}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Event Host
            </label>
            <div className="flex">
              <select
                {...register("rsvpEmail")}
                className="w-full h-[51px] px-3 rounded-l-lg border border-gray-300 outline-none"
                value={formData.rsvpEmail}
                onChange={handleChange}
              >
                <option value="">Self</option>
                {rsvps?.map((rsvp, index) => (
                  <option key={index} value={rsvp.email}>
                    {`${rsvp.firstName} ${rsvp.lastName}`}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setOpenRsvpDialog(true)}
                className="h-[51px] whitespace-nowrap px-4 rounded-r-lg cursor-pointer text-sm bg-lightGray font-medium text-blackie"
              >
                Add RSVP
              </button>
            </div>
            <FormErrorMessage error={errors.rsvpEmail} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Language
            </label>
            <select
              {...register("language", {
                required: "Language is required",
              })}
              value={formData.language}
              onChange={handleChange}
              className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
            >
              <option value="">Select Language</option>
              {Object.values(LANGUAGES).map((language, index) => {
                return <option key={index}>{language}</option>;
              })}
            </select>
            {errors.language && (
              <p className="text-red-500 text-sm">{errors.language.message}</p>
            )}
          </div>

          {!formData.isSession && (
            <div className="flex flex-col gap-1">
              <label className="text-label text-base font-semibold">
                Entry Fee
              </label>
              <input
                type="number"
                {...register("entryFee", {
                  required: "Entry Fee is required",
                  min: { value: 0, message: " Entry Fee cannot be negative" },
                  valueAsNumber: true,
                })}
                value={formData.entryFee}
                onChange={handleChange}
                className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
              />
              {errors.entryFee && (
                <p className="text-red-500 text-sm">
                  {errors.entryFee.message}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="grid  grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Physical Consideration
            </label>
            <textarea
              className="w-full  h-[100px] px-3 py-1 rounded-lg border border-gray-300 outline-none resize-none"
              {...register("physicalConsideration", {
                required: false,
              })}
              value={formData.physicalConsideration}
              onChange={handleChange}
            ></textarea>
            <FormErrorMessage error={errors.physicalConsideration} />
          </div>
        </div>
        <div className="grid  grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Special Instruction
            </label>
            <textarea
              className="w-full  h-[100px] px-3 py-1 rounded-lg border border-gray-300 outline-none resize-none"
              {...register("specialInstructions", {
                required: false,
              })}
              value={formData.specialInstructions}
              onChange={handleChange}
            ></textarea>
            <FormErrorMessage error={errors.specialInstructions} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {formData.eventBadge ? (
            <div
              className={`relative flex flex-col items-center justify-center pt-6 pb-2 px-6 rounded-lg cursor-pointer transition-all duration-300 border
                      "border-yellow-500"
                    }`}
            >
              <button
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                onClick={() => {
                  formData.badgeUrlId = 0;
                  formData.eventBadge = null;
                  setValue("badgeUrlId", 0, { shouldValidate: true });
                  setFormData({ ...formData });
                }}
              >
                <FaTrash size={18} />
              </button>

              <img
                className="w-[98px] min-h-[108px] object-cover transition-transform duration-200 hover:scale-105"
                src={formData.eventBadge.image.name}
              />

              <span className="text-lg font-semibold mt-3 text-black">
                {formData.eventBadge.badgeType}
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => setIsOpenBadgeModal(true)}
                className="h-[51px] mt-3 whitespace-nowrap px-4 rounded-lg cursor-pointer text-sm bg-lightGray font-medium text-blackie"
              >
                Upload Badge
              </button>
              {errors.badgeUrlId && (
                <p className="text-red-500 text-sm">
                  {errors.badgeUrlId.message}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Ambassador Support Section */}
          <div>
            <label className="text-label text-base font-semibold">
              Is there an ambassador you would like to invite to attend and
              support your event?
            </label>
            <div className="flex items-center space-x-6 mt-3">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  value="true"
                  checked={formData.ambassadorSupportRequired}
                  {...register("ambassadorSupportRequired", {
                    required: !params?.id,
                  })}
                  onChange={handleChangeAmbassadorSupport}
                  className="custom-radio"
                />
                <span>Yes</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  value="false"
                  checked={!formData.ambassadorSupportRequired}
                  {...register("ambassadorSupportRequired", {
                    required: !params?.id,
                  })}
                  onChange={handleChangeAmbassadorSupport}
                  className="custom-radio"
                />
                <span>No</span>
              </label>
            </div>
            <FormErrorMessage error={errors.ambassadorSupportRequired} />

            {formData.ambassadorSupportRequired && (
              <div className="mt-4 flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Select Ambassador Support Slots
                </label>
                <select
                  className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
                  {...register("ambassadosAllowed", { required: true })}
                  value={formData.ambassadosAllowed}
                  onChange={handleChange}
                >
                  <option value="">Select Slots</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                </select>
                <FormErrorMessage error={errors.ambassadosAllowed} />

                {formData.ambassadosAllowed == 1 && (
                  <div className="mt-4 flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      Ambassador One Refferal Code
                    </label>
                    <input
                      type="text"
                      placeholder="Enter ambassador one refferal code"
                      {...register("ambassadorOneCode", {
                        required: "Please enter ambassador one refferal code",
                      })}
                      value={formData.ambassadorOneCode}
                      onChange={handleChange}
                      className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
                    />
                    {errors.ambassadorOneCode && (
                      <p className="text-red-500 text-sm">
                        {errors.ambassadorOneCode.message}
                      </p>
                    )}
                  </div>
                )}

                {formData.ambassadosAllowed == 2 && (
                  <div className="mt-4 space-y-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-label text-base font-semibold">
                        Ambassador One Refferal Code
                      </label>
                      <input
                        type="text"
                        placeholder="Enter ambassador one refferal code"
                        {...register("ambassadorOneCode", {
                          required: "Please enter ambassador one refferal code",
                        })}
                        value={formData.ambassadorOneCode}
                        onChange={handleChange}
                        className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
                      />
                      {errors.ambassadorOneCode && (
                        <p className="text-red-500 text-sm">
                          {errors.ambassadorOneCode.message}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-label text-base font-semibold">
                        Ambassador Two Refferal Code
                      </label>
                      <input
                        type="text"
                        placeholder="Enter ambassador two refferal code"
                        {...register("ambassadorTwoCode", {
                          required: "Please enter ambassador two refferal code",
                        })}
                        value={formData.ambassadorTwoCode}
                        onChange={handleChange}
                        className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
                      />
                      {errors.ambassadorTwoCode && (
                        <p className="text-red-500 text-sm">
                          {errors.ambassadorTwoCode.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4">
          <button
            type="button"
            onClick={handleSubmit(() => onSubmit())}
            className="px-6 py-3 w-[120px] bg-primaryBlueBg text-white rounded-full"
          >
            Post
          </button>
          {!params?.id && (
            <button
              className=" py-3 whitespace-nowrap w-[150px] bg-fade text-primaryBlueText rounded-full"
              onClick={handleSubmit(() => onSubmit(EVENT_STATUS.DRAFT))}
            >
              Save for Later
            </button>
          )}
        </div>
      </form>
      {openRsvpDialog && (
        <AddRepresentativeModal
          isOpen={openRsvpDialog}
          onClose={() => setOpenRsvpDialog(false)}
          onAddComplete={() => getRsvps(setRsvps)}
        />
      )}

      {isOpenBadgeModal && (
        <BadgeModal
          isOpenBadgeModal={isOpenBadgeModal}
          onCloseBadgeModal={handleCloseBadgeDialog}
        />
      )}
    </div>
  );
};

export default CreateEvent;
